import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Button, Form, Image, Overlay, OverlayTrigger, Tooltip } from "react-bootstrap";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { useAppDispatch } from "../../../store/hooks";
import SpinnerLoader from "../../common/SpinnerLoader";
import { updateJob } from "../../../store/features/dashboard";
import { clearStoredDistance, markNeedsRegenerate } from "../../../utility/distancePrefs";
import { NUMBERS } from "../../../resources/number-constants";
import {
  backBtn,
  drag,
  greenArrowRight,
  deleteCandidateProp,
  scrollDownArrowIcon,
  infoWhite,
  blackInfo,
} from "../../../assets/images/images";
import {
  renderCustomCloseButton,
  renderErrorToast,
} from "../../../utility/helpers";

const MERGE_ALLOWED_BUCKETS = new Set(["Must-Haves", "Should-Haves", "Good-to-Haves"]);

const isMergeAllowedForBucketTitle = (title: string) =>
  MERGE_ALLOWED_BUCKETS.has(String(title || "").trim());

const move = (
  source: any,
  destination: any,
  droppableSource: any,
  droppableDestination: any
) => {
  const sourceClone = Array.from(source);
  const destClone = Array.from(destination);
  const [removed] = sourceClone.splice(droppableSource.index, 1);
  destClone.splice(droppableDestination.index, 0, removed);

  const result: any = {};
  result[droppableSource.droppableId] = sourceClone;
  result[droppableDestination.droppableId] = destClone;

  return result;
};

// Reorder function
const reorder = (list: any, startIndex: any, endIndex: any) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result;
};

const getItemStyle = (isDragging: any, draggableStyle: any) => ({
  background: isDragging ? "#3DCBB1" : "#EFEFEF",
  borderColor: isDragging ? "#3DCBB1" : "#EFEFEF",
  color: isDragging ? "#FFF" : "#292929",
  ...draggableStyle,
});

const getImageStyle = (isDragging: any) => ({
  // opacity: isDragging ? "0.3" : "1",
  filter: isDragging ? "invert(1)" : "invert(0)",
});

const getListStyle = (isDraggingOver: any) => ({
  background: isDraggingOver ? "#fff" : "#FFF",
});

const DraggableItem = ({
  jobData,
  idealizeJobData,
  loading,
  jobAction,
  setHavesData,
  handleClose
}: any) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [newItem, setNewItem] = useState("");
  const [state, setState] = useState<any>(idealizeJobData);
  const [clickedItems, setClickedItems] = useState<any>({});
  const [deactiveItems, setDeactiveItems] = useState<any>({});
  const [showInput, setShowInput] = useState<any>(
    idealizeJobData.map(() => false)
  );

  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [selectedSourceGroupIndex, setSelectedSourceGroupIndex] = useState<number | null>(null);
  const [selectedSourceItemIndex, setSelectedSourceItemIndex] = useState<number | null>(null);
  const [currentList, setCurrentList] = useState<any>("Must-Haves");

  const [showOverlay, setShowOverlay] = useState(false);
  const [showActionOverlay, setShowActionOverlay] = useState(false);
  const [actionOverlayMode, setActionOverlayMode] = useState<"combine" | "mergedItem" | null>(null);
  const [actionOverlayTarget, setActionOverlayTarget] = useState<any>(null);
  const [pendingCombineAction, setPendingCombineAction] = useState<any>(null);
  const [pendingMergedItemAction, setPendingMergedItemAction] = useState<any>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const targetRef = useRef<any>(null);
  const overlayRef = useRef<any>(null);
  const actionOverlayRef = useRef<any>(null);
  const scrollRef = useRef<any>(null);
  const [atBottom, setAtBottom] = useState(false);
  const otherBucketsRef = useRef<HTMLDivElement | null>(null);
  const [couldBucketHeight, setCouldBucketHeight] = useState<number | null>(null);
  const [bucketsLayoutHeight, setBucketsLayoutHeight] = useState<number | null>(null);
  const dragScrollRaf = useRef<number | null>(null);
  const dragActiveRef = useRef(false);
  const pointerClientYRef = useRef<number | null>(null);
  const cleanupPointerMoveRef = useRef<null | (() => void)>(null);
  const lastAtBottomRef = useRef(false);

  const getHeadingHelpText = (title: string) => {
    const normalized = (title || "").toLowerCase();
    if (normalized.includes("must")) {
      return "Non‑negotiable requirements. Candidates missing these shouldn’t proceed.";
    }
    if (normalized.includes("should")) {
      return "Important strengths that strongly improve fit but aren’t absolute blockers.";
    }
    if (normalized.includes("good")) {
      return "Valuable bonuses that differentiate strong candidates from the rest.";
    }
    if (normalized.includes("could")) {
      return "Optional nice‑to‑haves. Useful, but not expected for the role.";
    }
    if (normalized.includes("won't") || normalized.includes("wont")) {
      return "Traits or skills you want to avoid. Helps filter out mismatches early.";
    }
    return "Category that helps prioritize attributes for finding the right candidate.";
  };

  const couldGroupIndex: number = state.findIndex((group: any) =>
    String(group?.title || "").toLowerCase().includes("could")
  );

  const otherGroupIndices: number[] = state
    .map((_: any, idx: number) => idx)
    .filter((idx: number) => idx !== couldGroupIndex);

  const renderDroppableGroup = (group: any, ind: number) => (
    <Droppable key={ind} droppableId={`${ind}`} isCombineEnabled>
      {(provided, snapshot) => (
        <div className="drop-inner">
          <h4 className="drop-title">
            <span className="drop-title-text">{group.title}</span>
            <OverlayTrigger
              placement="top"
              overlay={<Tooltip>{getHeadingHelpText(group.title)}</Tooltip>}
            >
              <span className="info-icon-wrap" role="button" tabIndex={0}>
                <Image
                  src={blackInfo}
                  height={18}
                  width={18}
                  alt="Info"
                  className="info-icon info-icon-light"
                />
                <Image
                  src={infoWhite}
                  height={18}
                  width={18}
                  alt="Info"
                  className="info-icon info-icon-dark"
                />
              </span>
            </OverlayTrigger>
          </h4>
          <p className="drop-subtitle">
            {group.limit &&
              group.showLimit &&
              `Select max of ${group.limit}`}
            {group.showLimit && group.items.length === group.limit && (
              <>
                <em>(Remove one attribute to add more)</em>.
              </>
            )}
          </p>
          <div
            {...provided.droppableProps}
            ref={provided.innerRef}
            style={getListStyle(snapshot.isDraggingOver)}
            className="drop-container"
          >
            {group.items.map((item: any, index: any) => {
              const itemKey = `${ind}-${index}`;
              const isHighlighted = getHighlightedItems.indexOf(item) !== -1;
              return (
                <Draggable key={itemKey} draggableId={itemKey} index={index}>
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                      style={getItemStyle(
                        snapshot.isDragging,
                        provided.draggableProps.style
                      )}
                      className={`draggable-list ${deactiveItems[itemKey] ? "deactive" : ""} ${
                        isHighlighted ? "highlighted-item" : ""
                      }`}
                      id={itemKey}
                      onClick={() =>
                        handleOpenDetail(ind, index, item, group.title)
                      }
                    >
                      <Image
                        src={drag}
                        height={20}
                        width={20}
                        alt="drag"
                        style={getImageStyle(snapshot.isDragging)}
                      />
                      <span className={`text ${/\s+OR\s+/i.test(item) ? "merged-or-item" : ""}`}>
                        {/\s+OR\s+/i.test(item)
                          ? parseOrParts(item).map((part: string, partIndex: number, arr: string[]) => (
                              <span key={`${itemKey}-${part}-${partIndex}`} className="merged-or-part-wrap">
                                <span
                                  className="merged-or-part"
                                  title="Drag pill out"
                                  onMouseDown={(event) =>
                                    pullOutMergedPart(event, ind, index, part)
                                  }
                                >
                                  {part}
                                </span>
                                {partIndex < arr.length - 1 && (
                                  <span className="merged-or-separator">OR</span>
                                )}
                              </span>
                            ))
                          : item}
                      </span>
                      <Image
                        src={deleteCandidateProp}
                        height={20}
                        width={20}
                        alt="drag"
                        className="delete-drag"
                        style={{ cursor: isDeleting ? "not-allowed" : "pointer" }}
                        onClick={(event) => {
                          if (!isDeleting) {
                            handleItemClick(event, ind, index);
                          }
                        }}
                      />
                    </div>
                  )}
                </Draggable>
              );
            })}
            {provided.placeholder}
            {!showInput[ind] && group.items.length < group.limit && (
              <Button
                variant="link"
                className="text-green"
                onClick={() => handleAddMoreClick(ind)}
              >
                + add more
              </Button>
            )}
            {showInput[ind] && (
              <div className="input-container">
                <Form.Control
                  type="text"
                  placeholder="Type here"
                  value={newItem}
                  onChange={(e) => setNewItem(e.target.value)}
                  onBlur={() => handleInput(ind)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      e.stopPropagation();
                      handleBlur(ind);
                    }
                  }}
                  autoFocus
                />
                <Image
                  src={greenArrowRight}
                  height={30}
                  width={30}
                  alt="arrow"
                  className="submit-button"
                  onClick={() => handleBlur(ind)}
                />
              </div>
            )}
          </div>
        </div>
      )}
    </Droppable>
  );

  const updateChanges = async (updatedData: any) => {
    const payload: any = {
      user: jobData?.user,
      conversation_id: jobData?.conversation_id,
      job_conversation_id: jobData?.job_conversation_id,
      must_have: {
        title: updatedData[0]?.items,
      },
      should_have: {
        title: updatedData[1]?.items,
      },
      good_to_have: {
        title: updatedData[NUMBERS.TWO]?.items,
      },
      wont_have: {
        title: updatedData[NUMBERS.THREE]?.items,
      },
      could_have: {
        title: updatedData[NUMBERS.FOUR]?.items,
      },
    };

    try {
      // Any change in draggables should reset the candidate selection distance to the job's default
      clearStoredDistance(jobData?.id);
      await dispatch(updateJob({ payload, id: jobData?.id })).unwrap();
      // Mark that graph needs regeneration due to draggable/weights changes
      markNeedsRegenerate(jobData?.id);
    } catch (error) {
      console.info("Error while update changes", error);
    }
  };

  const removeHighlight = (itemText: string) => {
    const actionItems = localStorage.getItem('actionItems');
    if (actionItems) {
      const parsedActionData: string[]  = JSON.parse(actionItems);
      const filteredItems = parsedActionData.filter((item: string) => item !== itemText)
      localStorage.setItem('actionItems', JSON.stringify(filteredItems));
    }
  }

  const scrollToBottom = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({
        top: scrollRef.current.scrollHeight,
        behavior: "smooth",
      });
      setAtBottom(true);
    }
  };

  const scrollToTop = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({
        top: 0,
        behavior: "smooth",
      });
      setAtBottom(false);
    }
  };

  const handleScroll = () => {
    const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
    if (scrollTop + clientHeight === scrollHeight) {
      setAtBottom(true);
    } else if (scrollTop === 0) {
      setAtBottom(false);
    }
  };

  const parseOrParts = (value: string): string[] =>
    String(value || "")
      .split(/\s+OR\s+/i)
      .map((part) => part.trim())
      .filter(Boolean);

  const buildMergedItem = (targetItem: string, draggedItem: string): string => {
    const mergedParts = [...parseOrParts(targetItem), ...parseOrParts(draggedItem)];
    const deduped = mergedParts.filter(
      (part, idx) =>
        mergedParts.findIndex((x) => x.toLowerCase() === part.toLowerCase()) === idx
    );
    return deduped.join(" OR ");
  };

  const applySeparateFromCombine = (pending: any) => {
    const {
      sourceGroupIndex,
      sourceItemIndex,
      targetGroupIndex,
      targetItemIndex,
    } = pending;

    const draggedItem = state[sourceGroupIndex]?.items?.[sourceItemIndex];
    if (!draggedItem) return;

    const newState = [...state];
    const sourceItems = [...newState[sourceGroupIndex].items];
    const targetItems =
      sourceGroupIndex === targetGroupIndex
        ? sourceItems
        : [...newState[targetGroupIndex].items];

    sourceItems.splice(sourceItemIndex, 1);

    let insertIndex = targetItemIndex + 1;
    if (sourceGroupIndex === targetGroupIndex && sourceItemIndex < targetItemIndex) {
      insertIndex = targetItemIndex;
    }
    insertIndex = Math.max(0, Math.min(insertIndex, targetItems.length));
    targetItems.splice(insertIndex, 0, draggedItem);

    newState[sourceGroupIndex].items = sourceItems;
    newState[targetGroupIndex].items = targetItems;

    setState(newState);
    setHavesData(newState);
    updateChanges(newState);
  };

  const applyMergeFromCombine = (pending: any) => {
    const {
      sourceGroupIndex,
      sourceItemIndex,
      targetGroupIndex,
      targetItemIndex,
    } = pending;

    const draggedItem = state[sourceGroupIndex]?.items?.[sourceItemIndex];
    const currentTargetItem = state[targetGroupIndex]?.items?.[targetItemIndex];
    if (!draggedItem || !currentTargetItem) return;

    const newState = [...state];
    const sourceItems = [...newState[sourceGroupIndex].items];
    const targetItems =
      sourceGroupIndex === targetGroupIndex
        ? sourceItems
        : [...newState[targetGroupIndex].items];

    sourceItems.splice(sourceItemIndex, 1);

    let mergeIndex = targetItemIndex;
    if (sourceGroupIndex === targetGroupIndex && sourceItemIndex < targetItemIndex) {
      mergeIndex = targetItemIndex - 1;
    }
    if (mergeIndex < 0 || mergeIndex >= targetItems.length) {
      return;
    }

    targetItems[mergeIndex] = buildMergedItem(targetItems[mergeIndex], draggedItem);
    newState[sourceGroupIndex].items = sourceItems;
    newState[targetGroupIndex].items = targetItems;

    setState(newState);
    setHavesData(newState);
    updateChanges(newState);
  };

  const handleCombineChoice = (choice: "merge" | "separate") => {
    const pending = pendingCombineAction;
    setShowActionOverlay(false);
    setActionOverlayMode(null);
    setActionOverlayTarget(null);
    setPendingCombineAction(null);
    if (!pending) return;

    if (choice === "merge") {
      applyMergeFromCombine(pending);
      return;
    }
    applySeparateFromCombine(pending);
  };

  const onDragEnd = (result: any) => {
    stopManualDragScrolling();
    const { source, destination, combine } = result;

    if (combine) {
      const sourceGroupIndex = +source.droppableId;
      const sourceItemIndex = source.index;
      const targetGroupIndex = +combine.droppableId;
      const targetItemIndex = +(String(combine.draggableId).split("-")[1] || -1);

      if (
        sourceGroupIndex >= 0 &&
        targetGroupIndex >= 0 &&
        sourceItemIndex >= 0 &&
        targetItemIndex >= 0 &&
        state?.[sourceGroupIndex]?.items?.[sourceItemIndex]
      ) {
        const targetTitle = state[targetGroupIndex]?.title;
        if (!isMergeAllowedForBucketTitle(targetTitle)) {
          toast.info(
            "You can only merge skills with OR in Must-Haves, Should-Haves, and Good-to-Haves.",
            { closeButton: renderCustomCloseButton() }
          );
          applySeparateFromCombine({
            sourceGroupIndex,
            sourceItemIndex,
            targetGroupIndex,
            targetItemIndex,
          });
          return;
        }

        setPendingCombineAction({
          sourceGroupIndex,
          sourceItemIndex,
          targetGroupIndex,
          targetItemIndex,
        });
        setActionOverlayMode("combine");
        setActionOverlayTarget(
          document.getElementById(`${targetGroupIndex}-${targetItemIndex}`)
        );
        setShowActionOverlay(true);
      }
      return;
    }

    // Dropped outside the list
    if (!destination) {
      return;
    }

    if (state[source.droppableId].items[source.index]) {
      removeHighlight(state[source.droppableId].items[source.index])
    }

    const sInd = +source.droppableId;
    const dInd = +destination.droppableId;

    const destinationLimit = state[dInd].limit;

    if (sInd !== dInd && state[dInd].items.length >= destinationLimit) {
      renderErrorToast(
        `Cannot add more than ${destinationLimit} items to this list.`
      );
      return;
    }

    if (sInd === dInd) {
      const items = reorder(state[sInd].items, source.index, destination.index);
      const newState = [...state];
      newState[sInd].items = items;
      setState(newState);
      setHavesData(newState);
    } else {
      const actionResult = move(
        state[sInd].items,
        state[dInd].items,
        source,
        destination
      );
      const newState = [...state];
      newState[sInd].items = actionResult[sInd];
      newState[dInd].items = actionResult[dInd];

      // setState(newState.filter((group) => group.items.length));
      setState(newState);
      setHavesData(newState);

      updateChanges(newState);
    }
  };

  const performDragAutoScroll = () => {
    if (!dragActiveRef.current) return;
    const container = scrollRef.current;
    const clientY = pointerClientYRef.current;
    if (!container || clientY === null) return;

    const rect = container.getBoundingClientRect();
    const threshold = 110; // px from top/bottom to start scrolling

    const distTop = clientY - rect.top;
    const distBottom = rect.bottom - clientY;

    const maxTop = Math.max(0, container.scrollHeight - container.clientHeight);
    const currentTop = container.scrollTop;
    let nextTop = currentTop;

    // Scroll speed is proportional to how deep into the threshold we are.
    if (distTop < threshold) {
      const delta = Math.max(12, (threshold - distTop) * 0.35);
      nextTop = currentTop - delta;
    } else if (distBottom < threshold) {
      const delta = Math.max(12, (threshold - distBottom) * 0.35);
      nextTop = currentTop + delta;
    }

    nextTop = Math.max(0, Math.min(maxTop, nextTop));
    if (nextTop !== currentTop) {
      container.scrollTop = nextTop;
    }

    const atEnd =
      container.scrollTop + container.clientHeight >= container.scrollHeight - 2;
    if (lastAtBottomRef.current !== atEnd) {
      lastAtBottomRef.current = atEnd;
      setAtBottom(atEnd);
    }
  };

  const startManualDragScrolling = () => {
    if (dragActiveRef.current) return;
    dragActiveRef.current = true;

    const onPointerMove = (e: PointerEvent) => {
      pointerClientYRef.current = e.clientY;

      if (dragScrollRaf.current) return;
      dragScrollRaf.current = window.requestAnimationFrame(() => {
        dragScrollRaf.current = null;
        performDragAutoScroll();
      });
    };

    window.addEventListener("pointermove", onPointerMove, { passive: true });
    cleanupPointerMoveRef.current = () => {
      window.removeEventListener("pointermove", onPointerMove);
    };
  };

  const stopManualDragScrolling = () => {
    dragActiveRef.current = false;
    pointerClientYRef.current = null;

    if (cleanupPointerMoveRef.current) {
      cleanupPointerMoveRef.current();
      cleanupPointerMoveRef.current = null;
    }

    if (dragScrollRaf.current) {
      window.cancelAnimationFrame(dragScrollRaf.current);
      dragScrollRaf.current = null;
    }
  };

  // Keep "Could Haves" column height equal to the combined height of the
  // stacked buckets on the right, so the left list can scroll independently
  // without stretching the whole row.
  useLayoutEffect(() => {
    if (couldGroupIndex === -1) {
      setCouldBucketHeight(null);
      setBucketsLayoutHeight(null);
      return;
    }
    const el = otherBucketsRef.current;
    if (!el) return;

    const update = () => {
      const bucketNodes = el.querySelectorAll(".drop-inner");
      if (!bucketNodes || bucketNodes.length === 0) {
        const rect = el.getBoundingClientRect();
        const nextHeight = rect.height || null;
        setCouldBucketHeight(nextHeight);
        setBucketsLayoutHeight(nextHeight);
        return;
      }

      // Measure from the top of the first bucket to the bottom of the last bucket.
      // This avoids counting any extra wrapper/padding space that can change
      // after navigation and react-beautiful-dnd placeholders.
      const first = bucketNodes[0] as HTMLElement;
      const last = bucketNodes[bucketNodes.length - 1] as HTMLElement;
      const firstRect = first.getBoundingClientRect();
      const lastRect = last.getBoundingClientRect();

      const nextHeight = Math.max(0, lastRect.bottom - firstRect.top);
      // If we briefly measure 0 due to an intermediate layout frame, don't apply it.
      if (nextHeight < 10) return;

      setCouldBucketHeight(nextHeight);
      setBucketsLayoutHeight(nextHeight);
    };

    update();
    // On navigation (back/forward) the initial layout can briefly report
    // an intermediate height before DnD placeholders settle.
    // Re-check on the next paint frames to keep scrollbar behavior stable.
    requestAnimationFrame(() => update());
    requestAnimationFrame(() => update());

    const ResizeObs = (window as any).ResizeObserver;
    if (typeof ResizeObs === "function") {
      const ro = new ResizeObs(() => update());
      ro.observe(el);
      return () => ro.disconnect();
    }

    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, [couldGroupIndex]);

  useEffect(() => {
    return () => {
      // Ensure pointermove listener and RAF are cleaned up on unmount.
      stopManualDragScrolling();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAddMoreClick = (index: number) => {
    setNewItem("");
    let newShowInput = [...showInput];
    newShowInput = newShowInput.map(() => false);
    newShowInput[index] = true; // Show input for the clicked group
    setShowInput(newShowInput);
  };

 
  const handleBlur = (index: number) => {
    if (newItem.trim()) {
      saveInput(index);
    } else {
      hideInput(index);
    }
  };

  const hideInput = (index: number) => {
    const newShowInput = [...showInput];
    newShowInput[index] = false; // Hide input for the group
    setShowInput(newShowInput);
    setNewItem(""); // Reset input value
  };

  const saveInput = (index: number) => {
    const trimmed = newItem.trim();
    if (!trimmed) return;

    // Prevent duplicates within the same bucket.
    const existing = state?.[index]?.items || [];
    if (existing.includes(trimmed)) {
      hideInput(index);
      return;
    }

    const newState = [...state]; // Shallow copy of the state
    const updatedItems = [...newState[index].items]; // Deep copy of the items array for the specific group

    updatedItems.push(trimmed); // Add the new item to the copied items array

    newState[index].items = updatedItems; // Reassign the updated items array to the group
    setState(newState); // Update the state with the new state
    setHavesData(newState);
    hideInput(index); // Hide the input box after saving

    updateChanges(newState);
  };

  const handleOpenDetail = (
    groupIndex: number,
    itemIndex: number,
    item: string,
    groupTitle: any
  ) => {
    setCurrentList(groupTitle);
    setSelectedSourceGroupIndex(groupIndex);
    setSelectedSourceItemIndex(itemIndex);
    const itemKey = `${groupIndex}-${itemIndex}`;

    targetRef.current = document.getElementById(itemKey);
    setShowOverlay(!showOverlay);

    setSelectedItem(item);
  };

  const updateInprogress = () => {
    setIsDeleting(true);
    setTimeout(() => {
      setIsDeleting(false);
    }, NUMBERS.THREE_THOUSAND);
  };

  // Handle item click
  const handleItemClick = (
    event: any,
    groupIndex: number,
    itemIndex: number
  ) => {
    event.stopPropagation();
    const item = state?.[groupIndex]?.items?.[itemIndex];
    if (typeof item === "string" && /\s+OR\s+/i.test(item)) {
      setPendingMergedItemAction({ groupIndex, itemIndex, item });
      setActionOverlayMode("mergedItem");
      setActionOverlayTarget(event.currentTarget);
      setShowActionOverlay(true);
      return;
    }

    updateInprogress();

    const itemKey = `${groupIndex}-${itemIndex}`;
    if (clickedItems[itemKey]) {
      // If the item is already clicked, cancel the timeout
      clearTimeout(clickedItems[itemKey]);
      setClickedItems((prevState: any) => {
        const newState = { ...prevState };
        delete newState[itemKey];
        return newState;
      });
    } else {
      // If the item is clicked for the first time, set a timeout to remove it
      const timeoutId = setTimeout(() => {
        removeItem(groupIndex, itemIndex);
      }, NUMBERS.THREE_THOUSAND); // 3 seconds

      setDeactiveItems((prevState: any) => ({
        ...prevState,
        [itemKey]: true, // Mark the item as deactive
      }));

      setClickedItems((prevState: any) => ({
        ...prevState,
        [itemKey]: timeoutId,
      }));

      setTimeout(() => {
        setDeactiveItems((prevState: any) => {
          const newState = { ...prevState };
          delete newState[itemKey];
          return newState;
        });
      }, NUMBERS.THREE_THOUSAND);
    }
  };

  const removeItem = (groupIndex: number, itemIndex: number) => {
    const newState = [...state]; // Shallow copy of the state
    const groupItems = [...newState[groupIndex].items]; // Deep copy of the items array for the specific group

    groupItems.splice(itemIndex, 1); // Modify the deep-copied array
    newState[groupIndex].items = groupItems; // Assign the modified array back to the correct group
    setState(newState); // Update the state with the new state
    setHavesData(newState);

    updateChanges(newState);

    // Clean up clickedItems state
    setClickedItems((prevState: any) => {
      const newStateData = { ...prevState };
      const itemKey = `${groupIndex}-${itemIndex}`;
      delete newStateData[itemKey];
      return newStateData;
    });
  };

  const handleMergedItemAction = (action: "delete") => {
    const pending = pendingMergedItemAction;
    setShowActionOverlay(false);
    setActionOverlayMode(null);
    setActionOverlayTarget(null);
    setPendingMergedItemAction(null);
    if (!pending) return;

    const { groupIndex, itemIndex } = pending;
    if (action === "delete") {
      removeItem(groupIndex, itemIndex);
    }
  };

  const pullOutMergedPart = (
    event: React.MouseEvent,
    groupIndex: number,
    itemIndex: number,
    partToExtract: string
  ) => {
    event.preventDefault();
    event.stopPropagation();

    const group = state?.[groupIndex];
    const currentItem = group?.items?.[itemIndex];
    if (typeof currentItem !== "string" || !/\s+OR\s+/i.test(currentItem)) return;

    const parts = parseOrParts(currentItem);
    if (parts.length <= 1) return;

    const extractIndex = parts.findIndex(
      (part) => part.toLowerCase() === String(partToExtract).toLowerCase()
    );
    if (extractIndex === -1) return;

    const limit = group.limit;
    if (group.items.length >= limit) {
      renderErrorToast(`Cannot split because this list allows max of ${limit} items.`);
      return;
    }

    const extracted = parts[extractIndex];
    const remaining = parts.filter((_, idx) => idx !== extractIndex);
    const replacement = remaining.length > 1 ? remaining.join(" OR ") : remaining[0];

    const newState = [...state];
    const newItems = [...newState[groupIndex].items];
    newItems.splice(itemIndex, 1, replacement, extracted);
    newState[groupIndex].items = newItems;

    setState(newState);
    setHavesData(newState);
    updateChanges(newState);
  };

  const navigateTo = (route: any) => {
    navigate(route);
  };

  const moveItemToList = (targetList: string) => {
    if (targetList === currentList) {
      return;
    }

    const sourceListIndex =
      selectedSourceGroupIndex !== null
        ? selectedSourceGroupIndex
        : state.findIndex((list: any) => list.title === currentList);
    const targetListIndex = state.findIndex(
      (list: any) => list.title === targetList
    );

    if (sourceListIndex === -1 || targetListIndex === -1) {
      renderErrorToast("Error: Could not find the list");
      return;
    }

    // Create copies of the source and target list items arrays to avoid mutating state directly
    const sourceItems = [...state[sourceListIndex].items];
    const targetItems = [...state[targetListIndex].items];

    const sourceItemIndex =
      selectedSourceItemIndex !== null
        ? selectedSourceItemIndex
        : sourceItems.indexOf(selectedItem);

    if (sourceItemIndex !== -1 && sourceItemIndex < sourceItems.length) {
      sourceItems.splice(sourceItemIndex, 1); // Remove by index to avoid duplicates issues
    }

    setShowOverlay(!showOverlay);

    const targetLimit = state[targetListIndex].limit;
    if (targetItems.length >= targetLimit) {
      renderErrorToast(
        `Cannot add more than ${targetLimit} items to the target list.`
      );
      return;
    }

    targetItems.push(selectedItem); // Safely push the item to the copied targetItems array

    // Now update the state by creating new copies of the lists and updating the source/target
    const updatedLists = [...state];

    updatedLists[sourceListIndex] = {
      ...state[sourceListIndex],
      items: sourceItems, // Use the updated source items array
    };
    updatedLists[targetListIndex] = {
      ...state[targetListIndex],
      items: targetItems, // Use the updated target items array
    };

    // Update state with new lists
    setState(updatedLists);
    setHavesData(updatedLists);

    // Optionally, update backend or perform any further operations
    updateChanges(updatedLists);

    toast.success("Item moved successfully", {
      closeButton: renderCustomCloseButton(),
    });
  };

  const handleInput = (index: any) => {
    if (!newItem.trim()) {
      hideInput(index);
    }
  };

  const handleClickOutside = useCallback((event: MouseEvent) => {
    const targetNode = event.target as Node;
    const isOutsideMoveOverlay =
      overlayRef.current && !overlayRef.current.contains(targetNode);
    const isOutsideActionOverlay =
      actionOverlayRef.current && !actionOverlayRef.current.contains(targetNode);

    if (isOutsideMoveOverlay) {
      setShowOverlay(false);
    }
    if (showActionOverlay && isOutsideActionOverlay) {
      setShowActionOverlay(false);
      setActionOverlayMode(null);
      setActionOverlayTarget(null);
      setPendingCombineAction(null);
      setPendingMergedItemAction(null);
    }
  }, [showActionOverlay]);

  useEffect(() => {
    if (idealizeJobData.length) {
      setState(idealizeJobData);
      setHavesData(idealizeJobData);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idealizeJobData]);

  useEffect(() => {
    if (showOverlay || showActionOverlay) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showOverlay, showActionOverlay, handleClickOutside]);

  const savedHighlights = localStorage.getItem('actionItems');
  const getHighlightedItems = savedHighlights !== null ? JSON.parse(savedHighlights) : [];

  return (
    <>
      {!loading && (
        <div className="outer-section">
          <OverlayTrigger
                  placement="top"
                  overlay={
                      <Tooltip>
                      Back
                      </Tooltip>
                  }
          >
          <div
            className="back-btn"
            onClick={() => handleClose()}
          >
            <Image
              src={backBtn}
              height={25}
              width={25}
              alt="Back"
              className="back-btn-arrow"
            />
          </div>
          </OverlayTrigger>
        </div>
      )}
      <div 
        className="preview-left page-scroll"
        ref={scrollRef}
        onScroll={handleScroll}
      >
        {!loading && (
          <>
            <div className="preview-title d-none d-md-block">
              {jobAction !== "edit" && (
                <OverlayTrigger
                    placement="top"
                    overlay={
                        <Tooltip>
                        Back
                        </Tooltip>
                    }
                >
                <div
                  className="back-btn"
                  onClick={() =>
                    navigateTo(`/job-description?jobId=${jobData?.id}`)
                  }
                >
                  <Image
                    src={backBtn}
                    height={25}
                    width={25}
                    alt="Back"
                    className="back-btn-arrow"
                  />
                </div>
                </OverlayTrigger>
              )}
              {jobAction !== "edit" ? jobData?.title : "Edit Attributes"}{" "}
              <span>.</span>
            </div>
            <p className="preview-subtitle hide-on-mobile">
              Identify the ideal attributes for the candidate.
            </p>

            <div
              className={`scroll-down-btn ${atBottom ? "scroll-up" : "scroll-down"}`}
            >
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip>{atBottom ? "Go to Top" : "Go to Bottom"}</Tooltip>
                }
              >
                <Button
                  className="scroll-down"
                  onClick={atBottom ? scrollToTop : scrollToBottom}
                >
                  <Image
                    src={scrollDownArrowIcon}
                    height={48}
                    width={48}
                    alt="arrow"
                    className="scroll-down-arrow"
                  />
                </Button>
              </OverlayTrigger>
            </div>

            <div className="drag-drop-container">
              <DragDropContext onDragStart={startManualDragScrolling} onDragEnd={onDragEnd}>
                {couldGroupIndex !== -1 ? (
                  <div
                    className="idealize-buckets-layout"
                    style={
                      bucketsLayoutHeight !== null
                        ? { height: bucketsLayoutHeight }
                        : undefined
                    }
                  >
                    <div className="idealize-other-buckets" ref={otherBucketsRef}>
                      {otherGroupIndices.map((ind: number) =>
                        renderDroppableGroup(state[ind], ind)
                      )}
                    </div>
                    <div
                      className="idealize-could-bucket"
                      style={
                        couldBucketHeight !== null ? { height: couldBucketHeight } : undefined
                      }
                    >
                      {renderDroppableGroup(state[couldGroupIndex], couldGroupIndex)}
                    </div>
                  </div>
                ) : (
                  state.map((group: any, ind: any) =>
                    renderDroppableGroup(group, ind)
                  )
                )}
              </DragDropContext>
            </div>
          </>
        )}
        {loading && <SpinnerLoader />}
      </div>

      <Overlay
        target={targetRef.current}
        show={showOverlay}
        placement="bottom"
        ref={overlayRef}
      >
        <Tooltip id="overlay-example" className="move-option-tooltip">
          <ul className="move-option-ul">
            <li className="move-option-head">Move to</li>
            {state
              .filter((option: any) => option.title !== currentList)
              .map((option: any) => (
                <li
                  key={option.title}
                  className={
                    option?.items?.length === option?.limit
                      ? "move-option-disabled"
                      : ""
                  }
                  onClick={() => moveItemToList(option.title)}
                >
                  {option.title}
                </li>
              ))}
          </ul>
        </Tooltip>
      </Overlay>

      <Overlay
        target={actionOverlayTarget}
        show={showActionOverlay}
        placement="bottom"
        ref={actionOverlayRef}
      >
        <Tooltip id="action-overlay" className="move-option-tooltip right-click-style-tooltip">
          <ul className="move-option-ul">
            <li className="move-option-head">
              {actionOverlayMode === "combine" ? "Drop action" : "Merged group"}
            </li>
            {actionOverlayMode === "combine" ? (
              <>
                <li onClick={() => handleCombineChoice("merge")}>Merge</li>
                <li onClick={() => handleCombineChoice("separate")}>Separate</li>
              </>
            ) : (
              <>
                <li onClick={() => handleMergedItemAction("delete")}>Delete group</li>
              </>
            )}
          </ul>
        </Tooltip>
      </Overlay>
    </>
  );
};

export default DraggableItem;
